import { Spinner } from "@hackthenorth/north";
import { GraphQLError } from "graphql";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Prompt } from "react-router-dom";

import { PageWrapper } from "src/shared/components";
import { errorToast, successToast } from "src/shared/components/Toast";
import {
  ProfileContextProvider,
  useHackerContext,
  useProfileContext,
} from "src/shared/contexts";
import { Field, PageStage } from "src/shared/contexts/HackerContext/types";
import { useHackerState } from "src/shared/contexts/HackerContext/useHackerState";
import { useDeviceSize, useIsMounted } from "src/shared/hooks";
import ProfileForm from "src/views/profile/components/ProfileForm";
import {
  ProfileFormValues,
  TargetStage,
} from "src/views/profile/components/ProfileForm/types";
import { buildDiscoveryProfileMutationValues } from "src/views/profile/components/ProfileForm/values";

import RSVPSidebar from "../rsvp-sidebar";

import { buildProfileFormValues } from "./prefill";

const ACCEPTED_FIELDS = [
  Field.PREFERRED_NAME,
  Field.PRONOUNS,
  Field.CAREER_RSVP_STAGE,
  Field.PROFILE_RSVP_STAGE,
];

const ProfilePageContent: React.FC = () => {
  const isTabletOrSmaller = useDeviceSize("tablet");
  const isMounted = useIsMounted();
  const {
    updateResponses,
    navigateNext,
    navigateBack,
    isLoading: isHackerLoading,
    queueAutosave,
    cancelAutosave,
    waitForAutosave,
    hasUnsavedAutosave,
  } = useHackerContext();
  const { responsesState } = useHackerState(ACCEPTED_FIELDS, {});
  const {
    profile,
    isLoading: isProfileLoading,
    upsertDiscoveryProfile,
    autosaveDiscoveryProfile,
    isUpdating,
  } = useProfileContext();

  const [submitting, setSubmitting] = useState(false);

  const onValuesChange = useCallback(
    (stagingValues: ProfileFormValues) => {
      queueAutosave("directory-profile", async () => {
        await autosaveDiscoveryProfile(
          buildDiscoveryProfileMutationValues(stagingValues)
        );
      });
    },
    [autosaveDiscoveryProfile, queueAutosave]
  );

  const preferredName = responsesState[Field.PREFERRED_NAME];
  const pronouns = responsesState[Field.PRONOUNS];

  const formValues = useMemo(
    () =>
      buildProfileFormValues(profile, {
        preferredName,
        pronouns,
      }),
    [profile, preferredName, pronouns]
  );

  useEffect(() => {
    if (isMounted && submitting) {
      navigateNext();
    }
    if (responsesState[Field.CAREER_RSVP_STAGE] !== PageStage.COMPLETED) {
      navigateBack();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitting]);

  const completeRsvpStageAndAdvance = async () => {
    const { errors } = await updateResponses({
      [Field.PROFILE_RSVP_STAGE]: PageStage.COMPLETED,
    });

    if (errors) {
      throw new Error(
        "We weren't able to save your progress, please try again."
      );
    }

    successToast("Answers successfully saved!");
    setSubmitting(true);
  };

  const onSubmit = async (
    stagingValues: ProfileFormValues,
    targetStage: TargetStage
  ) => {
    try {
      cancelAutosave("directory-profile");
      await waitForAutosave();
      await upsertDiscoveryProfile({
        ...buildDiscoveryProfileMutationValues(stagingValues),
        stage_slug: targetStage,
      });

      await completeRsvpStageAndAdvance();
    } catch (e) {
      if (typeof e === "object" && e != null) {
        errorToast((e as GraphQLError)?.message ?? "Something went wrong.");
      } else {
        errorToast(String(e));
      }
    }
  };

  const onSkip = async () => {
    try {
      cancelAutosave("directory-profile");
      await waitForAutosave();
      await completeRsvpStageAndAdvance();
    } catch (e) {
      if (typeof e === "object" && e != null) {
        errorToast((e as GraphQLError)?.message ?? "Something went wrong.");
      } else {
        errorToast(String(e));
      }
    }
  };

  if (isProfileLoading || isHackerLoading) {
    return (
      <PageWrapper>
        <Spinner />
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <Prompt
        when={hasUnsavedAutosave && !submitting}
        message="You have unsaved changes, are you sure you want to move away from this page?"
      />
      {isTabletOrSmaller && <RSVPSidebar currentRoute="Directory Profile" />}
      <ProfileForm
        values={formValues}
        onSubmit={onSubmit}
        onValuesChange={onValuesChange}
        onSkip={onSkip}
        isSubmitting={isUpdating || submitting}
        mode="rsvp"
      />
    </PageWrapper>
  );
};

const HackerRsvpProfile: React.FC = () => (
  <ProfileContextProvider>
    <ProfilePageContent />
  </ProfileContextProvider>
);

export default HackerRsvpProfile;
